<template>
  <div
    data-component-group="business-form"
    data-component="merchants"
  >
    <slot name="beforeForm" />

    <div
      v-if="!isDisabled"
      class="component-header"
    >
      <magic-container>
        <v-autocomplete
          v-model="country"
          :items="threadSubmissions"
          prepend-inner-icon="public"
          label="Country"
          item-title="name"
          item-value="code"
          :return-object="true"
          required
          :readonly="isDisabled"
          :error-messages="countryErrors"
          @update:model-value="onChangeCountry"
          @blur="v$.country.$touch()"
        />
        <v-autocomplete
          v-model="merchant"
          :items="merchants"
          :loading="isLoadingMerchants"
          :label="$t('merchant')"
          :placeholder="$t('add_one_or_more_merchants')"
          item-title="name"
          :return-object="true"
          :readonly="isDisabled"
          required
          v-model:search="merchantSearch"
          :error-messages="merchantErrors"
          @blur="v$.merchant.$touch()"
        />
      </magic-container>

      <div class="component-actions">
        <Can
          action="update"
          subject="business"
        >
          <v-btn
            color="secondary"
            :loading="isLoading"
            @click="onUpdate"
          >
            <v-icon>edit</v-icon>
            {{ triggerLabel }}
          </v-btn>
        </Can>
      </div>
    </div>
    <div class="component-body">
      <Removables
        v-if="hasItems"
        :items="businessMerchants"
        :is-disabled="isDisabled || !$ability.can('update', 'business')"
        :on-remove="onRemoveMerchant"
      >
        <template v-slot:item="scope">
          <magic-container class="align-center">
            <div class="d-flex align-center">
              <v-icon>
                public
              </v-icon>
              {{ scope.item.country.name }}
            </div>
            <div class="d-flex align-center">
              <v-icon>
                add_business
              </v-icon>
              {{ scope.item.merchant.name }}
            </div>
          </magic-container>
        </template>
      </Removables>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import { v1 as uuidv1 } from 'uuid';
  import Removables from '../../lists/Removables';
  import { isObj, isStr, isNum, debounce } from '../../../utils';
  import { find } from 'lodash-es';

  const merchantSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    components: {
      Removables
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        country: {
          required
        },
        merchant: {
          required
        }
      };
    },
    props: {
      business: {
        type: Object,
        default: undefined
      },
      triggerLabel: {
        type: String,
        default: 'Add'
      },
      isDisabled: {
        type: Boolean,
        default: false
      },
      onSubmit: {
        type: Function,
        default: () => {}
      }
    },
    data() {
      return {
        country: null,
        merchant: null,
        merchants: [],
        merchantSearch: '',
        isLoading: false,
        isLoadingMerchants: false,
        isRefreshingMerchant: false,
      };
    },
    computed: {
      userCountries() {
        return this.$store.getters['users/countries'];
      },
      threadSubmissions() {
        const { threadSubmissions } = this.$store.getters['businesses/activeItem'] || {}  ;

        return threadSubmissions?.filter((item) => {
          return find(this.userCountries, { code: item.code });
        });
      },
      businessMerchants() {
        // activeItem or its businessMerchants can be missing; fall back to []
        // so hasItems() and the list rendering don't read .length of undefined.
        return this.$store.getters['businesses/activeItem']?.businessMerchants ?? [];
      },
      hasItems() {
        return this.businessMerchants.length > 0;
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Country is required';
          }
          return error.$message;
        });
      },
      merchantErrors() {
        return this.v$.merchant.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Merchant is required';
          }
          return error.$message;
        });
      }
    },
    watch: {
      'business.id': {
        immediate: true,
        handler(nextId, prevId) {
          if (nextId) {
            if (prevId !== undefined) {
              this.clear();
            }
            this.getBusinessMerchants(this.business);
          }
        }
      },
      merchant() {
        this.isRefreshingMerchant = true;
      },
      merchantSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingMerchant) {
            this.isRefreshingMerchant = false;
            return false;
          }

          merchantSearchDebouncer(() => this.getMerchants(nextSearch));
        }
      },
    },
    methods: {
      getMerchants(search) {
        if (!this.country?.code) {
          return;
        }

        const query = {
          search,
          countries: [this.country.code],
        };

        this.isLoadingMerchants = true;

        return this.$store.dispatch('pepper/getMerchants', query)
          .then((merchants) => {
            this.merchants = merchants;
            this.isLoadingMerchants = false;
            return merchants;
          })
          .catch((err) => {
            this.isLoadingMerchants = false;
            return err;
          });
      },
      getBusinessMerchants({ id }) {
        const query = { id };
        return this.$store.dispatch('businesses/getBusinessMerchants', query);
      },
      canSubmitRequest() {
        return isStr(this.country?.code)
          && isNum(this.business?.id)
          && isNum(this.merchant?.id);
      },
      onUpdate() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }
        this.isLoading = true;
        const { country, merchant } = this;
        const query = {
          merchant,
          country: country.code
        };

        this.onSubmit(query)
        .then((res)=> {
          this.clear();
          return res;
        })
        .catch(err => Promise.reject(err))
        .finally(()=>{
          this.isLoading = false;
        });
      },
      onRemoveMerchant(item) {
        const query = {
          id: this.business.id,
          merchant_id: item.id
        };

        return this.$store.dispatch('businesses/removeBusinessMerchant', query);
      },
      onChangeCountry() {
        this.merchant = null;
        this.merchants = [];
      },
      clear() {
        this.v$.$reset();
        this.country = null;
        this.merchant = null;
        this.merchants = [];
        this.merchantSearch = '';
        this.isLoadingMerchants = false;
        this.isRefreshingMerchant = false;
      }
    }
  };
</script>

<style lang="scss">
@use 'sass:map';
  @use '../../../assets/styles/theme' as *;

  [data-component-group="business-form"][data-component="merchants"] {
    padding: $spacing-xl;
    background-color: map.get($colors, shades, 'white');
    border-top: 1px solid map.get($colors, grey, lighten-2);

    .component-actions {
      padding: 0 0 16px 0;
      text-align: right;

      .v-btn {
        margin: 0;

        .v-icon {
          margin: 0 6px 0 0;
        }
      }
    }

    [data-component="removables"] {
      padding: 16px 0 0 0;

      .v-list {
        padding: 0;
      }

      .v-list-item {
        height: 62px;
        border-radius: $border-radius;
        background-color: map.get($colors, grey, lighten-3);
      }
    }
  }
</style>
